import Parse from "parse";

/**
 * Renew the login JWT instead of signing the user out when it expires.
 *
 * The refresh token is kept in localStorage beside the access token. It was an
 * httpOnly cookie, which is stronger against XSS, but the API and the UIs are on
 * different registrable domains: the cookie was third-party, so Safari dropped it
 * outright and rotation never engaged there at all.
 */

const JWT_AUTH_FAILURE_PATTERN = /authentication required|token expired|token revoked|invalid token/i;

/** The same refusals in the login page's vocabulary. */
const SIGN_OUT_REASONS: Record<string, string> = {
  expired: "idle",
  session_expired: "expired",
  token_reused: "reused",
};

const DEFAULT_SIGN_OUT_REASON = "expired";

/**
 * Where the reason waits for the login page to collect it.
 *
 * App renders the Router, so the logout handler sits above router context and cannot
 * navigate to carry this on a URL the way the customer portal does. One key, written
 * on the way out and cleared on the way in, keeps the behaviour without moving the
 * app's root around.
 */
const REASON_KEY = "signOutReason";

let signOutReason = DEFAULT_SIGN_OUT_REASON;
let sessionLost: (() => unknown) | null = null;
let ending = false;

/** Storage is absent during server rendering, so every read goes through this. */
const stored = (key: string) => (typeof localStorage === "undefined" ? null : localStorage.getItem(key));

/** The bearer this app is currently sending, or null before login restores one. */
const storedJwt = () => stored("authJwt");

/** Rotation spends this and returns its successor, so it is replaced on every renewal. */
const storedRefresh = () => stored("refreshToken");

/** The interceptor lives outside React, so it hands the teardown back here. */
export function onSessionLost(handler: () => unknown) {
  sessionLost = handler;
}

/**
 * Hand the teardown to the app, once. The teardown's own calls carry the dead session,
 * are refused with 209 and land back here, as do the other calls already in flight when
 * the session lapsed; without the guard each would start the teardown again. Released
 * when the teardown settles, by which point the session token is gone and nothing
 * matches isLostSession any more.
 */
export function endSession() {
  void endSessionOnce(() => sessionLost?.());
}

/**
 * Another tab's logout, told apart from a session that lapsed here: the login page would
 * otherwise say the session expired when the person signed out on purpose. Left alone
 * when a teardown is already running, whose reason is the one the login page shows.
 */
export function endSessionElsewhere() {
  if (!ending) signOutReason = "elsewhere";
  endSession();
}

/**
 * The guard itself, for the teardowns that do not start here: manual logout and the idle
 * timer. They make the same calls, refused with 209 the same way, so unguarded they set
 * off a second teardown through endSession that finishes last and overwrites their
 * reason. Whichever teardown starts first is the only one that runs, and its reason is
 * the one the login page shows.
 */
export function endSessionOnce(teardown: () => unknown): Promise<void> {
  if (ending) return Promise.resolve();
  ending = true;
  return Promise.resolve(teardown())
    .then(() => undefined)
    .finally(() => {
      ending = false;
    });
}

/** Left for the login page to show once, then forget. */
export function recordSignOutReason(reason?: string): void {
  const value = reason ?? signOutReason;
  signOutReason = DEFAULT_SIGN_OUT_REASON;
  try {
    sessionStorage.setItem(REASON_KEY, value);
  } catch (error: any) {
    // A browser refusing storage costs a message, not a logout — but a login page
    // that silently stops explaining itself is hard to account for, so say why.
    console.warn("Could not record the sign-out reason:", error?.message);
  }
}

/** Consumed on read, so a later visit to the login page is not still explaining itself. */
export function takeSignOutReason(): string | null {
  try {
    const value = sessionStorage.getItem(REASON_KEY);
    sessionStorage.removeItem(REASON_KEY);
    return value;
  } catch (error: any) {
    console.warn("Could not read the sign-out reason:", error?.message);
    return null;
  }
}

/**
 * A withAuth rejection on a request that carried a bearer. Before one is installed these
 * are the expected first-load race, and renewing would not help.
 */
export const isRenewableAuthFailure = (error: any) =>
  !!storedJwt() && error?.code === Parse.Error.SCRIPT_FAILED && JWT_AUTH_FAILURE_PATTERN.test(error?.message || "");

/**
 * The Parse session itself is gone, which renewal cannot mend: rotation slides a live
 * session, it does not resurrect a dead one. Matched by nothing above, a 209 was simply
 * rethrown — leaving a user on a page where every call failed, with no sign-out and no
 * way out but a manual reload.
 *
 * Guarded on a stored session token so a 209 raised before anyone has logged in cannot
 * bounce a visitor off the login page.
 */
export const isLostSession = (error: any) => !!stored("sessionToken") && error?.code === Parse.Error.INVALID_SESSION_TOKEN;

/**
 * "refused" is the server saying no, and ends the session. "failed" is not getting an
 * answer at all — offline, a 5xx while the API restarts, a body that is not JSON — and
 * ending the session over that turned a brief outage into a sign-out.
 */
export type RenewalOutcome = "renewed" | "refused" | "failed";

/**
 * Trade the refresh token for a fresh pair. The session token comes too: it slides the
 * Parse session, and is what the endpoint falls back to when there is no refresh token
 * yet — a session restored from before this shipped, for instance.
 */
async function rotateBearer(): Promise<RenewalOutcome> {
  const sessionToken = stored("sessionToken");
  const refreshToken = storedRefresh();

  try {
    const response = await fetch(`${process.env.REACT_APP_PARSE_SERVER_URL}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(sessionToken ? { "X-Parse-Session-Token": sessionToken } : {}),
      },
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });
    const body = await response.json().catch(() => ({}));

    // A refusal is a 401 that says why. Anything else short of a token is the call
    // failing, which is no grounds for signing anyone out.
    if (response.status === 401 && body?.reason) {
      signOutReason = SIGN_OUT_REASONS[body.reason] ?? DEFAULT_SIGN_OUT_REASON;
      console.warn("Token rotation refused:", body.reason);
      return "refused";
    }

    if (!response.ok || !body?.token) {
      console.error("Token rotation failed:", response.status);
      return "failed";
    }

    localStorage.setItem("authJwt", body.token);
    // The one it replaced is revoked; keeping it would fail the next renewal as a reuse.
    if (body.refreshToken) localStorage.setItem("refreshToken", body.refreshToken);
    Parse.CoreManager.set("REQUEST_HEADERS", { Authorization: `Bearer ${body.token}` });
    return "renewed";
  } catch (error: any) {
    console.error("Token rotation failed:", error?.message);
    return "failed";
  }
}

let renewal: Promise<RenewalOutcome> | null = null;

/**
 * Single-flight: rotation revokes what it spends, so concurrent renewals would race.
 * Every caller waiting on the one in flight sees the same outcome.
 */
export function renewAuthToken(): Promise<RenewalOutcome> {
  if (!renewal) {
    renewal = rotateBearer().finally(() => {
      renewal = null;
    });
  }
  return renewal;
}
